import { deriveClassed, VariantProps } from '@tw-classed/react';
import { ComponentProps } from 'react';

import LoadingDots from '@/components/v4/LoadingDots';
import { classedMerge } from '@/utils/classed-merge';

const ButtonBase = classedMerge(
  'button',
  'inline-flex items-center focus:outline-none *:pointer-events-none disabled:cursor-not-allowed font-medium',
  {
    variants: {
      variant: {
        contained: '',
        outlined: '',
        tonal: '',
        ghost: '',
        unset: '',
      },
      color: {
        blue: '',
        coolgray: '',
        orange: '',
        red: '',
        kakao: '',
        black: '',
        // v2 platform colors
        cpeats: '',
        yogiyo: '',
        baemin: '',
        ddangyo: '',
        naver: '',
        test: '',
        // v2 semantic colors
        primary: '',
        secondary: '',
      },
      height: {
        18: 'h-4.5 px-1.5 gap-0.5 rounded-[0.375rem]',
        20: 'h-5 px-2 gap-1 rounded-[0.25rem]',
        22: 'h-5.5 px-1.75 gap-1 rounded-[0.25rem]',
        24: 'h-6 px-2 gap-1 rounded-[0.375rem]',
        26: 'h-6.5 px-2.5 gap-1.5 rounded-[0.375rem]',
        28: 'h-7 px-2.5 gap-1.5 rounded-[0.5rem]',
        30: 'h-7.5 px-3 gap-1.5 rounded-[0.5rem]',
        32: 'h-8 px-3 gap-1.5 rounded-[0.5rem]',
        34: 'h-8.5 px-3.5 gap-1.5 rounded-[0.675rem]',
        35: 'h-[35px] px-3.5 gap-1.5 rounded-[0.675rem]',
        36: 'h-9 px-3.5 gap-1.5 rounded-[0.675rem]',
        38: 'h-9.5 px-3.5 gap-1.5 rounded-[0.675rem]',
        40: 'h-10 px-4 gap-1.5 rounded-[0.675rem]',
        42: 'h-10.5 px-4 gap-1.5 rounded-[0.675rem]',
        44: 'h-11 px-4 gap-1.5 rounded-[0.75rem]',
        46: 'h-11.5 px-4.5 gap-1.5 rounded-[0.75rem]',
        48: 'h-12 px-4.5 gap-1.5 rounded-[0.75rem]',
        50: 'h-12.5 px-5 gap-1.5 rounded-[0.875rem]',
        52: 'h-13 px-5 gap-1.5 rounded-[0.875rem]',
        54: 'h-13.5 px-5 gap-1.5 rounded-[0.875rem]',
        56: 'h-14 px-5 gap-1.5 rounded-[0.875rem]',
        58: 'h-14.5 px-5.5 gap-1.5 rounded-[0.875rem]',
        60: 'h-15 px-6 gap-1.5 rounded-[1rem]',
        unset: '',
      },
      fullWidth: {
        true: 'w-full',
        false: 'flex-shrink-0',
      },
      justify: {
        start: 'justify-start',
        center: 'justify-center',
        end: 'justify-end',
        between: 'justify-between',
        around: 'justify-around',
        evenly: 'justify-evenly',
        stretch: 'justify-stretch',
        normal: 'justify-normal',
      },
      cursor: {
        pointer: 'cursor-pointer',
        default: 'cursor-default',
        none: 'cursor-none',
      },
    },
    compoundVariants: [
      // contained
      {
        variant: 'contained',
        color: 'blue',
        class: 'bg-cds-blue-50 text-white border border-transparent disabled:bg-cds-blue-30',
      },
      {
        variant: 'contained',
        color: 'coolgray',
        class:
          'bg-cds-coolgray-80 text-white border border-transparent disabled:bg-cds-coolgray-30',
      },
      {
        variant: 'contained',
        color: 'orange',
        class: 'bg-cds-orange-50 text-white border border-transparent disabled:bg-cds-orange-30',
      },
      // tonal
      {
        variant: 'tonal',
        color: 'blue',
        class:
          'bg-cds-blue-20 text-cds-blue-50 border border-transparent disabled:bg-cds-blue-20 disabled:text-cds-blue-40',
      },
      {
        variant: 'tonal',
        color: 'coolgray',
        class:
          'bg-cds-coolgray-10 text-cds-coolgray-80 border border-transparent disabled:bg-cds-coolgray-10 disabled:text-cds-coolgray-40',
      },
      {
        variant: 'tonal',
        color: 'orange',
        class:
          'bg-cds-orange-20 text-cds-orange-50 border border-transparent disabled:bg-cds-orange-10 disabled:text-cds-orange-30',
      },
      // outlined
      {
        variant: 'outlined',
        color: 'blue',
        class:
          'bg-white border border-cds-blue-40 text-cds-blue-50 disabled:border-cds-blue-30 disabled:text-cds-blue-30',
      },
      {
        variant: 'outlined',
        color: 'coolgray',
        class:
          'bg-white border border-cds-coolgray-20 text-cds-coolgray-70 disabled:border-cds-coolgray-15 disabled:text-cds-coolgray-40',
      },
      {
        variant: 'tonal',
        color: 'red',
        class:
          'bg-cds-red-20 text-cds-red-55 border border-transparent disabled:bg-cds-red-10 disabled:text-cds-red-30',
      },
      {
        variant: 'contained',
        color: 'kakao',
        class: 'bg-kakao-bg text-kakao-text',
      },
      {
        variant: 'contained',
        color: 'black',
        class:
          'bg-cds-coolgray-90 text-white border border-transparent disabled:bg-cds-coolgray-40',
      },
      // v2 platform contained variants
      {
        variant: 'contained',
        color: 'cpeats',
        class: 'bg-[#0073E9] text-[#F7F8FA] border border-transparent',
      },
      {
        variant: 'contained',
        color: 'yogiyo',
        class: 'bg-[#FA0050] text-[#F7F8FA] border border-transparent',
      },
      {
        variant: 'contained',
        color: 'baemin',
        class: 'bg-[#2AC1BC] text-[#F7F8FA] border border-transparent',
      },
      {
        variant: 'contained',
        color: 'ddangyo',
        class: 'bg-ddangyo text-[#F7F8FA] border border-transparent',
      },
      {
        variant: 'contained',
        color: 'naver',
        class: 'bg-[#03C75A] text-white border border-transparent',
      },
      {
        variant: 'contained',
        color: 'test',
        class: 'bg-gray-1 text-white border border-transparent',
      },
      // v2 semantic contained variants
      {
        variant: 'contained',
        color: 'primary',
        class:
          'bg-primary text-[#F7F8FA] border border-transparent hover:bg-primary-hover disabled:bg-primary disabled:text-white disabled:opacity-60',
      },
      {
        variant: 'contained',
        color: 'secondary',
        class:
          'bg-secondary text-[#F7F8FA] border border-transparent hover:bg-secondary-hover disabled:bg-[#e3e9fb5c] disabled:text-secondary',
      },
      // v2 tonal variants
      {
        variant: 'tonal',
        color: 'primary',
        class: 'bg-primary-tonal text-primary border border-transparent',
      },
      {
        variant: 'tonal',
        color: 'secondary',
        class:
          'bg-secondary-tonal text-secondary border border-transparent disabled:bg-[#eff1f95a] disabled:text-[#8EA7EC]',
      },
      // v2 outlined variants
      {
        variant: 'outlined',
        color: 'primary',
        class: 'bg-transparent border border-primary-200 text-primary',
      },
    ],
    defaultVariants: {
      variant: 'contained',
      color: 'blue',
      height: 40,
      justify: 'center',
      fullWidth: false,
    },
  },
);

// Input 기준, sm 36, md 48, xl 54
export type ButtonHeight =
  | 18
  | 20
  | 22
  | 24
  | 26
  | 28
  | 30
  | 32
  | 34
  | 36
  | 38
  | 40
  | 42
  | 44
  | 46
  | 48
  | 50
  | 52
  | 54
  | 56
  | 58
  | 60
  | 'unset';

export type ButtonVariants = VariantProps<typeof ButtonBase>;

export type ButtonProps = ComponentProps<typeof ButtonBase> & {
  /** true면 LoadingDots를 렌더하고 버튼을 disabled 처리 (v2 Button과 동일 동작) */
  loading?: boolean;
  loadingDotClassName?: string;
};

/**
 * v4 Button. classedMerge 스타일 버튼(ButtonBase)에 loading 동작을 더한 래퍼.
 * deriveClassed로 감싸 `as` 다형성/variant 타입을 그대로 보존한다.
 * - loading=true → disabled + aria-disabled, 자식 뒤에 LoadingDots 렌더
 */
export const Button = deriveClassed<typeof ButtonBase, ButtonProps>(
  ({ loading, loadingDotClassName, disabled, children, ...props }, ref) => {
    const isDisabled = disabled || loading || undefined;
    return (
      <ButtonBase ref={ref} disabled={isDisabled} aria-disabled={isDisabled} {...props}>
        {children}
        {loading && <LoadingDots dotClassName={loadingDotClassName} />}
      </ButtonBase>
    );
  },
);

export { ButtonBase };

export default Button;
